/**
 * Release detection and update application.
 *
 * ## What this is
 *
 * The build writes `/release.json` at deploy time. The running client polls it,
 * compares the build id against the one it was compiled with, and surfaces an
 * update. Applying an update in the browser means reloading into the new
 * assets — but *when* that reload happens is the entire design problem, because
 * a reload during a voice call or mid-sentence is a bug, not a feature.
 *
 * ## Honest statement about signatures
 *
 * The manifest carries an Ed25519 signature and this module verifies it with a
 * pinned public key. For the **desktop** client that verification is the real
 * trust anchor: the updater downloads a package over the network and must not
 * install it unless it was signed by us.
 *
 * For the **web** client the same verification is worth much less, and it would
 * be dishonest to imply otherwise: an attacker who can replace `release.json`
 * on the origin can equally replace the JavaScript bundle that contains the
 * pinned key. The web client's real trust anchor is TLS plus the integrity of
 * the origin. Verification is kept here because it costs nothing, it catches
 * accidental corruption and misconfigured caches, and it means the desktop
 * updater shares one code path with the web one instead of having a second,
 * less-tested implementation.
 *
 * See docs/UPDATES.md.
 */

/** Injected at build time by Vite. Falls back for dev servers. */
const CURRENT_BUILD_ID: string = __BUILD_ID__;
const CURRENT_VERSION: string = __APP_VERSION__;

export interface ReleaseManifest {
  /** Unique per build. The only field that decides "is there an update". */
  buildId: string;
  /** Human-facing semver, shown in the UI and in release notes. */
  version: string;
  channel: 'stable' | 'beta' | 'canary';
  releasedAt: string;
  /**
   * Clients older than this must update before continuing. Reserved for
   * protocol-breaking or security-critical releases — using it casually trains
   * users to dismiss it, and it cannot be dismissed.
   */
  minimumSupportedVersion: string;
  /**
   * Percentage of clients that should take this release, 0-100. Lets a release
   * be held back and rolled forward without a redeploy, and rolled *back* by
   * setting it to 0.
   */
  rolloutPercentage: number;
  notesUrl?: string;
  summary?: string;
  /** base64 Ed25519 signature over the canonical serialisation below. */
  signature?: string;
}

export type UpdateUrgency = 'none' | 'optional' | 'required';

export interface UpdateState {
  urgency: UpdateUrgency;
  manifest: ReleaseManifest | null;
  currentVersion: string;
  currentBuildId: string;
  lastCheckedAt: number | null;
  checking: boolean;
  /** Set when the update is ready but application is being held back. */
  deferredReason: DeferReason | null;
}

export type DeferReason = 'voice-call' | 'unsent-draft' | 'user-postponed';

export const CURRENT: { buildId: string; version: string } = {
  buildId: CURRENT_BUILD_ID,
  version: CURRENT_VERSION,
};

// ---------------------------------------------------------------------------
// Version comparison
// ---------------------------------------------------------------------------

/**
 * Compares dotted numeric versions. Returns <0, 0, >0.
 *
 * Deliberately strict: a segment that is not a plain integer makes the
 * comparison return 0 ("cannot tell"), and callers treat "cannot tell" as
 * "do not force an update". Guessing at a malformed version and concluding the
 * user is out of date would lock people out of the app.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.');
  const pb = b.split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const sa = pa[i] ?? '0';
    const sb = pb[i] ?? '0';
    if (!/^\d+$/.test(sa) || !/^\d+$/.test(sb)) return 0;
    const na = Number(sa);
    const nb = Number(sb);
    if (na !== nb) return na < nb ? -1 : 1;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Rollout bucketing
// ---------------------------------------------------------------------------

/**
 * Stable per-install bucket in [0, 100).
 *
 * Derived from a random id generated once and kept in localStorage, so a given
 * install stays in or out of a staged rollout across reloads instead of
 * flickering. Not derived from anything identifying: a rollout bucket must not
 * become a tracking identifier.
 */
const BUCKET_KEY = 'aksolot.rollout.bucket';

export function rolloutBucket(storage: Storage = localStorage): number {
  try {
    const existing = storage.getItem(BUCKET_KEY);
    if (existing !== null && /^\d{1,2}$/.test(existing)) return Number(existing);
    const bytes = new Uint8Array(1);
    crypto.getRandomValues(bytes);
    const bucket = bytes[0]! % 100;
    storage.setItem(BUCKET_KEY, String(bucket));
    return bucket;
  } catch {
    // Private mode / storage disabled: always take the rollout rather than
    // never taking it, so a user with storage off still receives security fixes.
    return 0;
  }
}

// ---------------------------------------------------------------------------
// Manifest validation
// ---------------------------------------------------------------------------

const CHANNELS = new Set(['stable', 'beta', 'canary']);

/**
 * A release-notes link is attacker-influenced input that the UI turns into an
 * anchor. Allow exactly two shapes and nothing else:
 *
 *   `/changelog/1.4.0`      root-relative, therefore same-origin
 *   `https://host/path`     absolute, TLS only
 *
 * Everything else is rejected, including `javascript:`, `data:`, plain `http:`,
 * and protocol-relative `//host` — which looks root-relative but is not.
 *
 * Written without touching `location` so the rule is a pure function and can be
 * tested outside a browser. Depending on a browser global here once meant this
 * validator threw on the server and silently rejected every valid manifest.
 */
function isSafeNotesUrl(value: string): boolean {
  if (value.startsWith('//')) return false;
  if (value.startsWith('/')) return true;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validates a manifest fetched from the network. Everything here is untrusted
 * input; a malformed manifest must produce "no update", never a crash and never
 * a forced reload loop.
 */
export function parseManifest(value: unknown): ReleaseManifest | null {
  if (typeof value !== 'object' || value === null) return null;
  const m = value as Record<string, unknown>;

  if (typeof m['buildId'] !== 'string' || m['buildId'].length === 0 || m['buildId'].length > 128) return null;
  if (typeof m['version'] !== 'string' || !/^\d+\.\d+\.\d+(-[\w.]+)?$/.test(m['version'])) return null;
  if (typeof m['channel'] !== 'string' || !CHANNELS.has(m['channel'])) return null;
  if (typeof m['releasedAt'] !== 'string') return null;
  if (typeof m['minimumSupportedVersion'] !== 'string') return null;

  const rollout = m['rolloutPercentage'];
  if (typeof rollout !== 'number' || !Number.isFinite(rollout) || rollout < 0 || rollout > 100) return null;

  const notesUrl = m['notesUrl'];
  if (notesUrl !== undefined) {
    if (typeof notesUrl !== 'string' || !isSafeNotesUrl(notesUrl)) return null;
  }

  const summary = m['summary'];
  if (summary !== undefined && (typeof summary !== 'string' || summary.length > 300)) return null;

  const signature = m['signature'];
  if (signature !== undefined && typeof signature !== 'string') return null;

  return {
    buildId: m['buildId'],
    version: m['version'],
    channel: m['channel'] as ReleaseManifest['channel'],
    releasedAt: m['releasedAt'],
    minimumSupportedVersion: m['minimumSupportedVersion'],
    rolloutPercentage: rollout,
    ...(typeof notesUrl === 'string' ? { notesUrl } : {}),
    ...(typeof summary === 'string' ? { summary } : {}),
    ...(typeof signature === 'string' ? { signature } : {}),
  };
}

/**
 * Canonical bytes that the signature covers. Field order is fixed here rather
 * than taken from the JSON, because JSON key order is not guaranteed and a
 * signature over a re-serialised object would otherwise fail intermittently.
 */
export function canonicalPayload(m: ReleaseManifest): Uint8Array<ArrayBuffer> {
  const canonical = [
    m.buildId,
    m.version,
    m.channel,
    m.releasedAt,
    m.minimumSupportedVersion,
    String(m.rolloutPercentage),
  ].join('\n');
  const encoded = new TextEncoder().encode(canonical);
  // Copied into an unshared ArrayBuffer for the same reason as base64ToBytes.
  const out = new Uint8Array(new ArrayBuffer(encoded.byteLength));
  out.set(encoded);
  return out;
}

/**
 * Verifies the Ed25519 signature. Returns `'unsigned'` when no key is
 * configured or the manifest carries no signature — the caller decides what
 * that means, and it means different things on web and desktop.
 */
export async function verifyManifest(
  manifest: ReleaseManifest,
  publicKeyBase64: string | undefined,
): Promise<'valid' | 'invalid' | 'unsigned'> {
  if (publicKeyBase64 === undefined || publicKeyBase64 === '' || manifest.signature === undefined) {
    return 'unsigned';
  }
  try {
    const key = await crypto.subtle.importKey(
      'raw',
      base64ToBytes(publicKeyBase64),
      { name: 'Ed25519' },
      false,
      ['verify'],
    );
    const ok = await crypto.subtle.verify(
      'Ed25519',
      key,
      base64ToBytes(manifest.signature),
      canonicalPayload(manifest),
    );
    return ok ? 'valid' : 'invalid';
  } catch {
    // Ed25519 unavailable in this browser, or malformed key material. Not a
    // reason to claim the manifest is forged.
    return 'unsigned';
  }
}

/**
 * Backed by a real `ArrayBuffer` rather than the default `ArrayBufferLike`,
 * because WebCrypto refuses a possibly-shared buffer and the resulting type
 * error is otherwise silenced with a cast that hides the reason.
 */
function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------------------
// Decision
// ---------------------------------------------------------------------------

export interface DecisionInput {
  manifest: ReleaseManifest;
  currentBuildId: string;
  currentVersion: string;
  bucket: number;
  signatureStatus: 'valid' | 'invalid' | 'unsigned';
}

/**
 * Decides what to do about a fetched manifest.
 *
 * Fail-closed on a bad signature (ignore the release entirely) but fail-open on
 * an unknown rollout state (treat as not-in-rollout, i.e. no nag). The
 * asymmetry is deliberate: refusing a possibly-forged update is safe, while
 * refusing a legitimate one merely delays it.
 */
export function decideUpdate(input: DecisionInput): UpdateUrgency {
  const { manifest, currentBuildId, currentVersion, bucket, signatureStatus } = input;

  if (signatureStatus === 'invalid') return 'none';

  // Same build: nothing to do. This is the common case on every poll.
  if (manifest.buildId === currentBuildId) return 'none';

  // A client running *newer* than the manifest happens during a rollback or
  // when a stale CDN copy is served. Never nag someone to downgrade.
  if (compareVersions(currentVersion, manifest.version) > 0) return 'none';

  // Below the floor: blocking. Checked before the rollout gate on purpose —
  // a security release must reach everyone, not a percentage.
  if (compareVersions(currentVersion, manifest.minimumSupportedVersion) < 0) return 'required';

  if (bucket >= manifest.rolloutPercentage) return 'none';

  return 'optional';
}
